import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Table, { TableColumn } from '../../../../components/organisms/table';
import MVCHeader from '../../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import Actions from '../../../../components/molecules/actions';
import { ModalProps } from '../../../../components/template/mvc-modal';
import classNames from 'classnames';
import YardService, { IYard } from '../../../../services/Yard.service';
import YardsModal from './modal/yard';
import HarborService, { IHarbor } from '../../../../services/Harbor.service';
import Select from '../../../../components/atoms/select';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { useRouteLoaderData } from 'react-router-dom';
import { IUser } from '../../../../services/User.service';
import Button from '../../../../components/atoms/button';
import Text from '../../../../components/atoms/text';
import { setMaxIdleHTTPParsers } from 'http';
import HarborsModal from './modal/harbor';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import ModalConfirmation from '../../../../components/organisms/modal-confirmation';
import SVGIcon from '../../../../components/atoms/icons';

const yardInitialState: IYard = {
	name: '',
	isActive: true,
};

const harborInitialState: IHarbor = {
	name: '',
	isActive: true,
	address: '',
	yards: [],
};

const HarborsYards = () => {
	const { t } = useTranslation();
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const harborsModalTitles = {
		create: t('admin.harbors.new'),
		edit: t('admin.harbors.edit'),
		view: t('admin.harbors.view'),
	};

	const yardsModalTitles = {
		create: t('port_management.yards.new'),
		edit: t('port_management.yards.edit'),
		view: t('port_management.yards.view'),
	};

	const [harbors, setHarbors] = useState<IHarbor[]>([]);
	const [harborSelected, setHarborSelected] =
		useState<IHarbor>(harborInitialState);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const [harborsModalProps, setHarborsModalProps] = useState<
		ModalProps<IHarbor>
	>({
		data: harborInitialState,
		title: harborsModalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setHarborsModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: harborInitialState,
			}));
			if (reload) fetchHarborsData();
		},
	});

	const [yardsModalProps, setYardsModalProps] = useState<ModalProps<IYard>>({
		data: yardInitialState,
		title: yardsModalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: () => {
			setYardsModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: yardInitialState,
			}));
		},
	});

	const [isDeleteModalVisible, setIsDeleteModalVisible] =
		useState<boolean>(false);
	const [dataToDelete, setDataToDelete] = useState<IYard | null>(null);

	const fetchHarborsData = useCallback(async () => {
		if (harborsModalProps.isModalVisible) return;
		const response = await HarborService.getAll({
			idClient: Number(accountId),
		});
		if (!response) return;
		setHarbors(response.data);
		if (harborSelected.id !== undefined) {
			const harbor =
				response.data.find((item) => item.id === harborSelected.id) ||
				harborInitialState;
			setHarborSelected(harbor);
		}
		if (!user.permissions.isSystemAdmin) {
			const harbor =
				response.data.find(
					(item) => item.client?.id === user.client?.id
				) || harborInitialState;
			setHarborSelected(harbor);
		}
	}, [harborsModalProps.isModalVisible]);

	useEffect(() => {
		fetchHarborsData();
	}, [fetchHarborsData]);

	const fetchYardsData = useCallback(async () => {
		if (
			!harborSelected ||
			!harborSelected.id ||
			yardsModalProps.isModalVisible
		)
			return;
		const response = await YardService.getAll({
			idHarbor: harborSelected.id,
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
		});
		if (!response) return;
		setHarborSelected((prev) => ({
			...prev,
			yards: response.data,
		}));
		setPaginationOptions((prev) => ({
			...prev,
			totalItems: response.totalItems,
			totalPages: response.totalPages,
		}));
	}, [
		paginationOptions.pageCurrent,
		paginationOptions.pageSize,
		harborSelected.id,
		yardsModalProps.isModalVisible,
	]);

	useEffect(() => {
		fetchYardsData();
	}, [fetchYardsData]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = useCallback(
		(data: IYard) => {
			setYardsModalProps((prev) => ({
				...prev,
				title: yardsModalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[yardsModalTitles.view]
	);

	const onActionsEditClick = useCallback(
		(data: IYard) => {
			setYardsModalProps((prev) => ({
				...prev,
				title: yardsModalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[yardsModalTitles.edit]
	);

	const onCreateClick = useCallback(() => {
		setYardsModalProps((prev) => ({
			...prev,
			title: yardsModalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: yardInitialState,
		}));
	}, [yardsModalTitles.create]);

	const columns: TableColumn<IYard>[] = [
		{ key: 'name', label: t('misc.name') },
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
					onDeleteClick={
						user.permissions.isSystemAdmin
							? () => onActionsDeleteClick(row)
							: undefined
					}
				/>
			),
		},
	];

	const onCreateHarborClick = useCallback(() => {
		setHarborsModalProps((prev) => ({
			...prev,
			title: harborsModalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: harborInitialState,
		}));
	}, [harborsModalTitles.create]);

	const onEditHarborClick = useCallback(() => {
		setHarborsModalProps((prev) => ({
			...prev,
			title: harborsModalTitles.edit,
			mode: 'edit',
			isModalVisible: true,
			data: harborSelected,
		}));
	}, [harborsModalTitles.create, harborSelected]);

	const onActionsDeleteClick = async (row: IYard) => {
		setDataToDelete(row);
		setIsDeleteModalVisible(true);
	};

	const onDeleteModalClose = () => {
		setIsDeleteModalVisible(false);
		setDataToDelete(null);
	};

	const onDeleteConfirm = async () => {
		if (!dataToDelete || !dataToDelete.id) return;
		await YardService.delete(dataToDelete.id);
		fetchYardsData();
		NotificationToast.Success(
			`${t('port_management.yard')} ${t('alert.success.delete')}`
		);
		onDeleteModalClose();
	};

	const handleHarbor = (e: EditableChangeEvent) => {
		const harbor = harbors.find((item) => item.name === e.target.value);
		setHarborSelected(harbor || harborInitialState);
	};

	return (
		<>
			<div className="flex-grow grid grid-rows-[auto_auto_1fr_auto] p-4 gap-y-4">
				<div className="grid sm:grid-cols-2 gap-y-2">
					<Select
						className="max-w-96"
						name="harborSelected"
						value={harborSelected.name}
						label={t('port_management.harbor')}
						placeholder={t('port_management.harbor')}
						options={harbors.map((item) => item.name)}
						onChange={handleHarbor}
					/>
					<div className="flex justify-end gap-4">
						<Button onClick={() => onCreateHarborClick()}>
							{t('admin.harbors.new')}
						</Button>
						<Button
							onClick={() => onEditHarborClick()}
							disabled={!harborSelected.id}
						>
							{t('admin.harbors.edit')}
						</Button>
					</div>
					<Text>
						<b>{t('misc.name')}: </b>
						{harborSelected.name}
					</Text>
					<Text>
						<b>{t('admin.client')}: </b>
						{harborSelected.client?.fantasyName}
					</Text>
					<Text className="col-span-full">
						<b>{t('misc.address')}: </b>
						{harborSelected.address}
					</Text>
				</div>
				<MVCHeader
					title={t('port_management.yards.list')}
					onCreateText={t('port_management.yards.new')}
					onCreateClick={onCreateClick}
					onCreateDisabled={harborSelected.id == undefined}
				/>

				<div className="overflow-x-auto w-full">
					<Table
						data={harborSelected.yards}
						columns={columns}
						className={classNames('grid-cols-[1fr_auto]')}
					/>
				</div>

				<Pagination
					{...paginationOptions}
					onUpdate={onPaginationUpdate}
				/>
			</div>

			<HarborsModal
				{...harborsModalProps}
				setModalProps={setHarborsModalProps}
			/>
			<YardsModal
				{...yardsModalProps}
				harbor={harborSelected}
				setModalProps={setYardsModalProps}
			/>
			<ModalConfirmation
				onConfirm={onDeleteConfirm}
				onCancel={onDeleteModalClose}
				isModalVisible={isDeleteModalVisible}
			>
				<div className="flex flex-col items-center text-center gap-4">
					<SVGIcon
						icon="Error"
						className="h-12 w-auto fill-red stroke-white"
					/>
					<Text>
						{t('alert.confirmation.delete')}{' '}
						<b>{dataToDelete?.name}</b>?{' '}
						{t('alert.confirmation.yard')}
					</Text>
				</div>
			</ModalConfirmation>
		</>
	);
};

export default HarborsYards;
